import { useState } from 'react'
import { ConfirmDialog } from './ConfirmDialog'
import { ShowKeyOnce } from './ShowKeyOnce'
import { useRegenerateAccessKey } from '../api'
import { useNotify } from './Notifications'

interface RegenerateButtonProps {
  userId: string
  label?: string | null
}

/**
 * Two-phase affordance: confirm → reveal. Guarded by `ConfirmDialog` so a
 * misclick doesn't silently kick the operator whose key was just regenerated.
 * The plaintext reveal is intentionally the *same* component the create
 * dialog uses (see `ShowKeyOnce`) so the warning text stays consistent.
 */
export function RegenerateButton({ userId, label }: RegenerateButtonProps) {
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [regeneratedKey, setRegeneratedKey] = useState<string | null>(null)
  const mutation = useRegenerateAccessKey()
  const notify = useNotify()

  const doRegenerate = () => {
    mutation.mutate(userId, {
      onSuccess: data => {
        setConfirmOpen(false)
        setRegeneratedKey(data.key)
      },
      onError: err => {
        setConfirmOpen(false)
        notify(err.message || 'Не удалось перегенерировать ключ', 'error')
      },
    })
  }

  return (
    <>
      <button
        onClick={e => { e.stopPropagation(); setConfirmOpen(true) }}
        className="text-xs font-medium px-2.5 py-1 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
      >
        Перегенерировать
      </button>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={doRegenerate}
        title="Перегенерировать ключ?"
        message={`${label ? `«${label}». ` : ''}Старый ключ перестанет работать немедленно. Оператор будет выкинут из сессии на следующем запросе.`}
        confirmLabel="Перегенерировать"
        isPending={mutation.isPending}
      />

      {regeneratedKey && (
        <ShowKeyOnce
          open
          plaintext={regeneratedKey}
          title="Ключ обновлён"
          subtitle="Передайте новый ключ оператору. Он будет показан только сейчас."
          onClose={() => setRegeneratedKey(null)}
        />
      )}
    </>
  )
}
